<template>
  <div class="trip-log-details-view">
    <div>
      <TableSearch
        placeholder="Search for a trip by ID"
        :value="tripId ? tripId.toString() : ''"
        @searchSubmitted="onSearchSubmitted"
      />
    </div>
    <div v-if="tripId" class="trip-log-details-view__header">
      <h3 class="trip-log-details-view__title">Trip ID {{ tripId }}</h3>
      <div v-if="tripLength" class="trip-log-details-view__item">
        <span class="trip-log-details-view__item-label">RFR:</span>
        <span class="trip-log-details-view__item-value">{{
          tripLength?.RfrName
        }}</span>
      </div>
      <div v-if="tripLength" class="trip-log-details-view__item">
        <span class="trip-log-details-view__item-label">RFR Address:</span>
        <span class="trip-log-details-view__item-value">{{
          tripLength?.RfrAddress
        }}</span>
      </div>
      <div v-if="tripLength" class="trip-log-details-view__item">
        <span class="trip-log-details-view__item-label">Trip Length:</span>
        <span class="trip-log-details-view__item-value"
          >{{ tripLength?.Days }} day(s)</span
        >
      </div>
      <div class="trip-log-details-view__actions">
        <Button themeColor="primary" @click="exportToExcel" type="button"
          >Export to Excel</Button
        >
        <Button themeColor="primary" @click="printPage" type="button"
          >Print the page</Button
        >
      </div>
    </div>
    <ExpensesView v-if="tripId" :tripId="tripId" />
    <TripReportsGrid v-if="tripId" :tripId="tripId" />
    <TripAttachmentsView v-if="tripId" :tripId="tripId" />
  </div>
</template>

<script setup lang="ts">
import type { TripLength } from "@/Domain/TripLength";
import ExpensesView from "./ExpensesView.vue";
import TripReportsGrid from "./TripReportsGrid.vue";
import { onBeforeMount, ref } from "vue";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { api } from "@/api";
import TableSearch from "@/shared/ui/inputs/TableSearch.vue";
import TripAttachmentsView from "./TripAttachmentsView.vue";
import { Button } from "@progress/kendo-vue-buttons";
import { useExportStore } from "@/stores/useExportStore";
import { workbookOptions } from "@progress/kendo-vue-excel-export";
import { setQueryParameter } from "@/shared/helpers/urlHelper";

const urlParams = new URLSearchParams(window.location.search);

const exportStore = useExportStore();

const tripId = ref<number>(parseInt(urlParams.get("tripId") ?? "0"));

const tripLength = ref<TripLength>();

const getTripLengthAsync = async function () {
  return handleResponsePromiseAsync(
    api.get("GetTripLength", {
      tripId: tripId.value,
    }),
    (data) => {
      tripLength.value = data;
    },
    "TripLogDetailsView.vue",
    "getTripLengthAsync"
  );
};

const onSearchSubmitted = function (searchValue: string) {
  const value = parseInt(searchValue);
  if (!value) return;
  tripId.value = value;
  getTripLengthAsync();
  setQueryParameter("tripId", tripId.value.toString());
};

const exportToExcel = function () {
  const options = workbookOptions({
    columns: [
      { field: "tripId", title: "Trip ID" },
      { field: "rfrName", title: "RFR" },
      { field: "rfrAddress", title: "RFR Address" },
      { field: "days", title: "Trip Length" },
    ],
    data: [
      {
        tripId: tripId.value,
        rfrName: tripLength.value?.RfrName,
        rfrAddress: tripLength.value?.RfrAddress,
        days: tripLength.value?.Days,
      },
    ],
  });

  exportStore.exportExcel(
    tripLength.value
      ? `${tripLength.value.RfrLastName}, ${tripLength.value.RfrFirstName}, TripID ${tripId.value}`
      : `TripID ${tripId.value}`,
    options
  );
};

const printPage = function () {
  window.print();
};

onBeforeMount(() => {
  if (tripId.value) getTripLengthAsync();
});
</script>

<style lang="scss">
.trip-log-details-view {
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media print {
    .table-search {
      display: none;
    }

    .trip-log-details-view__actions {
      display: none;
    }
  }

  .k-column-title,
  .k-table-td {
    word-break: break-word;
  }

  &__header {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 16px;
  }

  &__title {
    font-size: 20px;
    font-weight: bold;
  }

  &__actions {
    display: flex;
    gap: 8px;
  }

  &__item {
    display: flex;
    gap: 8px;
    align-items: center;

    &-label {
      font-weight: bold;
    }

    &-value {
      font-weight: normal;
    }
  }
}
</style>
